import { useQuery } from '@tanstack/react-query';

/**
 * The Google Fonts family list, as a static asset.
 *
 * Generated by `scripts/refresh-google-fonts.mjs` and committed at
 * `apps/web/public/google-fonts.json`, so it is served from our own origin with no API call, no
 * auth, and nothing for a billing hold or a cold container to break. See that script for why it is
 * a file rather than a route.
 */
const CATALOG_URL = '/google-fonts.json';

/**
 * Families the design server has the actual font files for, hard-coded as the floor.
 *
 * Only used if the catalog fetch fails — a picker that can offer nothing at all is worse than one
 * offering the five that are guaranteed to render correctly. Matches `apps/api/assets/fonts`.
 */
const VENDORED_FALLBACK = ['Inter', 'Montserrat', 'Barlow', 'Barlow Condensed', 'Rubik'];

async function fetchCatalog(signal?: AbortSignal): Promise<string[]> {
  const res = await fetch(CATALOG_URL, { signal });
  if (!res.ok) throw new Error(`${CATALOG_URL} returned ${res.status}`);
  const data: unknown = await res.json();
  if (!Array.isArray(data) || data.length === 0) throw new Error('font catalog is empty');
  return data.filter((f): f is string => typeof f === 'string');
}

/**
 * The catalog, loaded once per session and shared by all three typography fields.
 *
 * `staleTime: Infinity` because a file that only changes when someone commits a new one has
 * nothing to revalidate against; react-query also dedupes the three simultaneous callers into one
 * request.
 */
export function useFontCatalog(): { families: string[]; loaded: boolean } {
  const query = useQuery({
    queryKey: ['google-fonts'],
    queryFn: ({ signal }) => fetchCatalog(signal),
    staleTime: Infinity,
    retry: 1,
  });
  return {
    families: query.data ?? (query.isError ? VENDORED_FALLBACK : []),
    // SUCCESS ONLY. This is what licenses the editor to say "that isn't a Google font", and the
    // five-family fallback is a floor for the picker, not a catalog anything can be judged
    // against — treating a failed fetch as "loaded" would accuse Roboto of not existing.
    loaded: query.isSuccess,
  };
}
